import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { generatePath, useLocation, useNavigate } from 'react-router-dom';
import {
  ajvResolver,
  apiAbortController,
  ResponseError,
  responseErrorsResolver,
  SelectItemProps,
} from '@ui/src';
import {
  useBindExternalOrganizationMutation,
  useLazyGetPspIntegrationsOrganizationsByNameQuery,
} from '../../../customers/store/services/customers';
import { ExternalOrganizationFormValues } from '../types';
import {
  OrganizationType,
  ExternalPspPartner,
  PspIntegrationPayment,
  PspIntegration,
  PspIntegrationOrganization,
} from '../../types';

import ROUTES from '../../../../routes';

const schema = {
  type: 'object',
  properties: {
    externalId: {
      type: 'string',
      minLength: 1,
      errorMessage: 'External Customer is required',
    },
    pspPartnerId: {
      type: 'string',
      minLength: 1,
      errorMessage: 'Bank is required',
    },
  },
  required: ['externalId', 'pspPartnerId'],
};

const useLinkedClientsForm = ({
  customerId,
  type,
  selectedPspPartnerIntegrationId,
  setExternalPanelIsOpen,
  setLinkExternalCustomerPopupVisible,
  pspPartners,
  pspIntegrations,
  selectedPartner,
  linkExternalCustomerPopupVisible,
}: {
  customerId?: string;
  type: OrganizationType;
  selectedPspPartnerIntegrationId?: string;
  setExternalPanelIsOpen?: (data: boolean) => void;
  setLinkExternalCustomerPopupVisible?: (data: boolean) => void;
  pspPartners?: ExternalPspPartner[];
  pspIntegrations?: PspIntegration[];
  selectedPartner?: string;
  linkExternalCustomerPopupVisible?: boolean;
}) => {
  const [panelIsOpen, setPanelIsOpen] = useState(false);
  const [globalError, setGlobalError] = useState('');
  const [dirtyPopupVisible, setDirtyPopupVisible] = useState(false);
  const [selectedPspIntegrationId, setSelectedPspIntegrationId] = useState('');
  const [availablePspPartners, setAvailablePspPartners] = useState<
    SelectItemProps[]
  >([]);
  const [pspIntegrationOrganizationsList, setPspIntegrationOrganizationsList] =
    useState<PspIntegrationOrganization[]>([]);
  const [
    externalOrganizationsListVisible,
    setExternalOrganizationsListVisible,
  ] = useState(false);
  const [externalOrganizationSearch, setExternalOrganizationSearch] =
    useState('');

  const {
    control,
    handleSubmit,
    setError,
    formState: { errors, isDirty },
    watch,
    setValue,
  } = useForm<ExternalOrganizationFormValues>({
    resolver: ajvResolver(schema),
  });

  const pspPartnerId = watch('pspPartnerId');
  const externalId = watch('externalId');

  const { search } = useLocation();
  const navigate = useNavigate();

  const [bindExternalOrganization, { isLoading, isSuccess }] =
    useBindExternalOrganizationMutation();
  const [
    getPspIntegrationOrganizations,
    { isLoading: organizationsIsLoading, isFetching: organizationsIsFetching },
  ] = useLazyGetPspIntegrationsOrganizationsByNameQuery();

  const onBindExternalOrganization = handleSubmit(async (values) => {
    setGlobalError('');

    const response = (await bindExternalOrganization({
      organizationId: customerId as string,
      organizationType: type,
      externalId: values?.externalId,
      active: true,
      pspPartnerId: values.pspPartnerId,
    })) as { error?: ResponseError };

    if (response?.error) {
      responseErrorsResolver({
        errors: response.error,
        setError,
        setGlobalError,
      });
    }
  });

  const getPspIntegrationOrganizationsAction = async ({
    name,
  }: {
    name: string;
  }) => {
    apiAbortController.abortPendingRequest();

    const response = await getPspIntegrationOrganizations(
      `?pspIntegrationId=${selectedPspIntegrationId || selectedPspPartnerIntegrationId}&name=${name}&take=40`,
    );

    if (response?.data?.data) {
      const nextOrganizationsList = response?.data?.data;

      setPspIntegrationOrganizationsList(nextOrganizationsList);
    }
  };

  const handleClose = () => {
    setPanelIsOpen(false);
    setSelectedPspIntegrationId('');
    setExternalOrganizationsListVisible(false);
    setPspIntegrationOrganizationsList([]);
    setExternalOrganizationSearch('');
    setGlobalError('');
    setDirtyPopupVisible(false);

    if (!setExternalPanelIsOpen) {
      let link = '';

      if (type === OrganizationType.INDIVIDUAL) {
        link = generatePath(
          ROUTES.CUSTOMERS_INDIVIDUAL_EXTERNAL_LINKED_CLIENTS,
          {
            customerId: customerId || '',
          },
        );
      } else {
        link = generatePath(
          ROUTES.CUSTOMERS_CORPORATE_EXTERNAL_LINKED_CLIENTS,
          {
            customerId: customerId || '',
          },
        );
      }

      setTimeout(() => {
        navigate(`${link}${search || ''}`);
      }, 100);
    }

    if (setExternalPanelIsOpen) {
      setExternalPanelIsOpen(true);
    }

    if (setLinkExternalCustomerPopupVisible) {
      setLinkExternalCustomerPopupVisible(false);
    }
  };

  useEffect(() => {
    if (pspPartners && pspIntegrations) {
      const nextPspPartners = pspPartners.map((item) => {
        const result = { ...item };

        if (
          result.pspIntegrationId &&
          pspIntegrations?.find(
            (integration) => integration.id === result.pspIntegrationId,
          )
        ) {
          const currentItegration = pspIntegrations?.find(
            (integration) => integration.id === result.pspIntegrationId,
          );

          if (currentItegration) {
            result.pspIntegration = {
              name: currentItegration.name,
              pspIntegrationPayments:
                currentItegration.pspIntegrationPayments as PspIntegrationPayment[],
              id: currentItegration.id,
              externalCustomersSupported:
                currentItegration.externalCustomersSupported,
            };
          }
        }

        return result;
      });

      const pspPartnersWithExternalCustomerSupport = nextPspPartners?.filter(
        (item) => item?.pspIntegration?.externalCustomersSupported,
      );

      if (pspPartnersWithExternalCustomerSupport?.length) {
        setAvailablePspPartners(
          pspPartnersWithExternalCustomerSupport?.map((item) => ({
            name: item.companyName,
            value: item.id,
          })),
        );
      }
    }
  }, [pspPartners, pspIntegrations, selectedPartner]);

  useEffect(() => {
    if (pspPartnerId && availablePspPartners && pspIntegrations) {
      const currentPartner = pspPartners?.find(
        (item) => item.id === pspPartnerId || item.id === selectedPartner,
      );

      if (currentPartner && currentPartner?.pspIntegrationId) {
        const currentIntegration = pspIntegrations?.find(
          (item) => item.id === currentPartner?.pspIntegrationId,
        );

        if (currentIntegration) {
          setSelectedPspIntegrationId(currentIntegration.id);
        }
      }
    }
  }, [pspPartnerId, availablePspPartners, pspIntegrations, selectedPartner]);

  useEffect(() => {
    if (
      externalOrganizationSearch &&
      (organizationsIsLoading || organizationsIsFetching)
    ) {
      setExternalOrganizationsListVisible(true);
    }
  }, [
    externalOrganizationSearch,
    organizationsIsLoading,
    organizationsIsFetching,
  ]);

  useEffect(() => {
    if (!setExternalPanelIsOpen) {
      setPanelIsOpen(true);
    }
  }, [setExternalPanelIsOpen]);

  useEffect(() => {
    if (linkExternalCustomerPopupVisible) {
      setPanelIsOpen(true);

      if (selectedPartner) {
        setValue('pspPartnerId', selectedPartner);
      }
    }
  }, [linkExternalCustomerPopupVisible, selectedPartner]);

  useEffect(() => {
    if (isSuccess) {
      handleClose();
    }
  }, [isSuccess]);

  return {
    panelIsOpen,
    isDirty,
    setDirtyPopupVisible,
    handleClose,
    globalError,
    isLoading,
    externalId,
    onBindExternalOrganization,
    control,
    availablePspPartners,
    errors,
    setExternalOrganizationsListVisible,
    externalOrganizationSearch,
    getPspIntegrationOrganizationsAction,
    pspPartnerId,
    pspIntegrationOrganizationsList,
    externalOrganizationsListVisible,
    organizationsIsLoading,
    organizationsIsFetching,
    setExternalOrganizationSearch,
    dirtyPopupVisible,
  };
};

export default useLinkedClientsForm;
